// Bridges HR data (contracts + payroll movements) into the treasury
// cash-flow engine. The Cash Outlook needs the same 12-month payroll
// projection that HrCostSimulationPage shows ; this helper extracts
// the calculation in pure form so both surfaces stay in sync.
//
// Output : { gross[12], net[12] } in SCENARIO ORDER (index 0 = first
// month of the scenario, regardless of calendar). The net is computed
// as gross × (1 - empl_cs_rate/100), the standard payroll approximation.
//
// La masse brute d'un mois RÉUTILISE `contractMassInMonth` (salaryMass) :
// salaire mensuel + 13e/14e positionnés sur leur mois de versement. Source
// UNIQUE partagée avec People — l'ancienne formule maison ne sommait que
// `monthly_salary`, donc oubliait le 13e/14e (charges sociales cash sous-
// estimées). `extra` = config tenant [mois_13e, mois_2e_13e|null, mois_14e].
import { contractMassInMonth, contractActiveInMonth } from '../../hr/salaryMass';

const EXTRA_DEFAULT = [12, null, 6]; // convention suisse standard (13e déc, 14e juin)

function parseLocalDate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function baseMonthlyMass(contracts, year, monthIdx, extra = EXTRA_DEFAULT) {
  return (contracts || []).reduce(
    (acc, c) => acc + contractMassInMonth(c, year, monthIdx, extra),
    0
  );
}

// Δ des mouvements NOMINATIFS (person_id défini) sur un mois donné.
// rate_change : nouveau taux absolu (mv.fte) × salaire 100% du contrat.
// departure   : supprime la contribution entière du contrat actif.
// Les mouvements synthétiques (sans person_id) restent dans movementImpactOnCell.
export function namedMovementDelta(contracts, movements, year, monthIdx, extra) {
  const cell = new Date(year, monthIdx, 1);

  // Contrat actif par personne dans ce mois (le plus récent si plusieurs)
  const contractByPerson = {};
  for (const c of contracts) {
    if (!c.person_id) continue;
    if (!contractActiveInMonth(c, year, monthIdx)) continue;
    const prev = contractByPerson[c.person_id];
    if (!prev || (c.start_date || '') > (prev.start_date || '')) {
      contractByPerson[c.person_id] = c;
    }
  }

  // Collecte des mouvements actifs par personne
  const departed = new Set();
  const rateChange = {}; // person_id → dernier mv rate_change actif

  for (const mv of movements) {
    if (!mv.person_id) continue;
    const sd = parseLocalDate(mv.start_month);
    if (!sd) continue;

    if (mv.movement_type === 'departure' && cell >= sd) {
      departed.add(mv.person_id);
    }
    if (mv.movement_type === 'rate_change') {
      const ed = mv.end_month ? parseLocalDate(mv.end_month) : null;
      if (cell >= sd && (!ed || cell <= ed)) {
        rateChange[mv.person_id] = mv; // dernier match gagne
      }
    }
  }

  let delta = 0;
  for (const [pid, contract] of Object.entries(contractByPerson)) {
    const baseMass = contractMassInMonth(contract, year, monthIdx, extra);
    if (departed.has(pid)) {
      delta -= baseMass;
    } else if (rateChange[pid]) {
      const ar = Number(contract.activity_rate || 0);
      if (ar > 0) {
        // delta = baseMass × (newRate / currentRate − 1) — valide aussi pour 13e/14e
        delta += baseMass * (Number(rateChange[pid].fte || 0) / ar - 1);
      }
    }
  }
  return delta;
}

function movementImpactOnCell(mv, year, monthIdx) {
  const sd = parseLocalDate(mv.start_month);
  if (!sd) return 0;
  const ed = mv.end_month ? parseLocalDate(mv.end_month) : null;
  const sign = mv.movement_type === 'departure' ? -1 : +1;
  const amt = Number(mv.monthly_amount || 0) * sign;
  const cell = new Date(year, monthIdx, 1);
  if (mv.movement_type === 'bonus') {
    return (sd.getFullYear() === year && sd.getMonth() === monthIdx) ? amt : 0;
  }
  if (cell < sd) return 0;
  if (ed && cell > ed) return 0;
  return amt;
}

// EPT (équivalent plein temps) effectif pour un mois donné, mouvements inclus.
// Même logique que namedMovementDelta mais pour les taux d'activité, pas les CHF.
export function eptForMonth({ contracts = [], movements = [], year, monthIdx }) {
  const cell = new Date(year, monthIdx, 1);

  const contractByPerson = {};
  for (const c of contracts) {
    if (!c.person_id || !contractActiveInMonth(c, year, monthIdx)) continue;
    const prev = contractByPerson[c.person_id];
    if (!prev || (c.start_date || '') > (prev.start_date || '')) {
      contractByPerson[c.person_id] = c;
    }
  }

  const departed = new Set();
  const rateChange = {};
  for (const mv of movements) {
    if (!mv.person_id) continue;
    const sd = parseLocalDate(mv.start_month);
    if (!sd) continue;
    if (mv.movement_type === 'departure' && cell >= sd) departed.add(mv.person_id);
    if (mv.movement_type === 'rate_change') {
      const ed = mv.end_month ? parseLocalDate(mv.end_month) : null;
      if (cell >= sd && (!ed || cell <= ed)) rateChange[mv.person_id] = mv;
    }
  }

  let ept = 0;
  for (const [pid, contract] of Object.entries(contractByPerson)) {
    if (departed.has(pid)) continue;
    ept += rateChange[pid]
      ? Number(rateChange[pid].fte || 0)
      : Number(contract.activity_rate || 0);
  }
  return Math.round(ept * 100) / 100; // 2 décimales
}

// Gross salary mass for a specific calendar month (year, monthIdx 0=Jan).
// Same logic as the loop body in computePayrollFromHr — exposed so callers
// (e.g., the assurance phasing in TreasuryPage) can fetch a "January gross"
// reference for any year without spinning a full 12-month projection.
export function grossForMonth({ contracts = [], movements = [], year, monthIdx, extra = EXTRA_DEFAULT }) {
  let v = baseMonthlyMass(contracts, year, monthIdx, extra);
  // Mouvements synthétiques (sans person_id : embauche ouverte, réduction générale)
  for (const mv of movements) {
    if (!mv.person_id) v += movementImpactOnCell(mv, year, monthIdx);
  }
  // Mouvements nominatifs (rate_change + departure liés à une personne)
  v += namedMovementDelta(contracts, movements, year, monthIdx, extra);
  return Math.round(v);
}

// startMonth : 0..11 (0=Jan, 4=May, …)
// startYear  : e.g. 2026
// emplRatePct: average employee social-charge rate as a percentage
//              (e.g. 7.2 for 7.2 %). 0 = no deduction → net = gross.
export function computePayrollFromHr({
  contracts = [],
  movements = [],
  startMonth = 0,
  startYear = new Date().getFullYear(),
  emplRatePct = 0,
  extra = EXTRA_DEFAULT,
}) {
  const gross = Array(12).fill(0);
  for (let k = 0; k < 12; k += 1) {
    const cellMonth = (startMonth + k) % 12;
    const cellYear = startYear + Math.floor((startMonth + k) / 12);
    let v = baseMonthlyMass(contracts, cellYear, cellMonth, extra);
    for (const mv of movements) {
      if (!mv.person_id) v += movementImpactOnCell(mv, cellYear, cellMonth);
    }
    v += namedMovementDelta(contracts, movements, cellYear, cellMonth, extra);
    gross[k] = v;
  }
  const netFactor = 1 - (Number(emplRatePct) || 0) / 100;
  const net = gross.map((v) => Math.round(v * netFactor));
  return { gross: gross.map((v) => Math.round(v)), net };
}
